import { AccountGroupService } from '../account-group';
import { FeatureFlagService } from '../core/feature-flag.service';
import { TranslateService } from '@ngx-translate/core';
import {
  AddToListMultiRowAction,
  AddToListSelectAllAction,
  AddToListSingleRowAction,
  ListActionsService,
} from '@galaxy/crm/integrations/dynamic-lists-actions';
import { combineLatest, map, Observable, switchMap } from 'rxjs';
import { MultiRowAction, SingleRowAction } from '@galaxy/crm/static';
import { DynamicListsFeature } from './constants';
import { BulkDeleteService, BulkDeleteAll, ExportActionsService, ExportSelectAllRowAction } from '@galaxy/crm/dynamic';
import { CRMAccessService } from './access.service';
import {
  AutomationActionsService,
  AutomationMultiRowAction,
  AutomationSelectAllRowAction,
  AutomationSingleRowAction,
  EphemeralAutomationMultiRowAction,
  EphemeralAutomationSelectAllRowAction,
  EphemeralAutomationSingleRowAction,
} from '@galaxy/crm/integrations/automation';

export function buildCustomObjectSingleRowTableActions(
  accountGroupService: AccountGroupService,
  featureFlagService: FeatureFlagService,
  listActionsService: ListActionsService,
  accessService: CRMAccessService,
  automationActionService: AutomationActionsService,
  currentUserId$: Observable<string>,
): Observable<SingleRowAction[]> {
  return accountGroupService.currentAccountGroup$.pipe(
    switchMap((currentAccountGroup) => {
      return combineLatest([
        accountGroupService.currentAccountGroupId$,
        featureFlagService.checkFeatureFlagsMulti(currentAccountGroup.partnerId, '', [DynamicListsFeature]),
        accessService.canRunManualAutomations$,
        currentUserId$,
      ]);
    }),
    map(([accountGroupId, featureFlags, canRunAutomations, currentUserId]) => {
      const canAddToList = featureFlags[DynamicListsFeature];
      const actions: SingleRowAction[] = [];
      if (canAddToList) {
        actions.push(
          AddToListSingleRowAction(accountGroupId, listActionsService, 'CustomObject', 'CRM.ADD_TO_STATIC_LIST'),
        );
      }
      if (canRunAutomations) {
        actions.push(
          AutomationSingleRowAction(
            accountGroupId,
            automationActionService,
            'CustomObject',
            'COMPANY.LIST_OBJECTS_TABLE.ACTIONS.START_AUTOMATION',
          ),
        );
        actions.push(
          EphemeralAutomationSingleRowAction(
            accountGroupId,
            automationActionService,
            currentUserId,
            'CustomObject',
            'COMPANY.LIST_OBJECTS_TABLE.ACTIONS.RUN_BULK_ACTION',
          ),
        );
      }
      return actions;
    }),
  );
}

export function buildCustomObjectMultiRowTableActions(
  accountGroupService: AccountGroupService,
  featureFlagService: FeatureFlagService,
  translationSvc: TranslateService,
  listActionsService: ListActionsService,
  accessService: CRMAccessService,
  automationActionService: AutomationActionsService,
  currentUserId$: Observable<string>,
): Observable<MultiRowAction[]> {
  return accountGroupService.currentAccountGroup$.pipe(
    switchMap((currentAccountGroup) => {
      return combineLatest([
        accountGroupService.currentAccountGroupId$,
        featureFlagService.checkFeatureFlagsMulti(currentAccountGroup.partnerId, '', [DynamicListsFeature]),
        accessService.canRunManualAutomations$,
        currentUserId$,
      ]);
    }),
    map(([accountGroupId, featureFlags, canRunAutomations, currentUserId]) => {
      const canAddToList = featureFlags[DynamicListsFeature];
      const actions: MultiRowAction[] = [];
      if (canAddToList) {
        actions.push(
          AddToListMultiRowAction(
            accountGroupId,
            listActionsService,
            'CustomObject',
            translationSvc.instant('CRM.ADD_TO_STATIC_LIST'),
          ),
        );
      }
      if (canRunAutomations) {
        actions.push(
          AutomationMultiRowAction(
            accountGroupId,
            automationActionService,
            'CustomObject',
            'COMPANY.LIST_OBJECTS_TABLE.ACTIONS.START_AUTOMATION',
          ),
        );
        actions.push(
          EphemeralAutomationMultiRowAction(
            accountGroupId,
            automationActionService,
            currentUserId,
            'CustomObject',
            'COMPANY.LIST_OBJECTS_TABLE.ACTIONS.RUN_BULK_ACTION',
          ),
        );
      }
      return actions;
    }),
  );
}

export function buildCustomObjectSelectAllTableActions(
  accountGroupService: AccountGroupService,
  featureFlagService: FeatureFlagService,
  translationSvc: TranslateService,
  listActionsService: ListActionsService,
  bulkDeleteService: BulkDeleteService,
  exportActionsService: ExportActionsService,
  accessService: CRMAccessService,
  automationActionService: AutomationActionsService,
  currentUserId$: Observable<string>,
): Observable<MultiRowAction[]> {
  return accountGroupService.currentAccountGroup$.pipe(
    switchMap((currentAccountGroup) => {
      return combineLatest([
        accountGroupService.currentAccountGroupId$,
        featureFlagService.checkFeatureFlagsMulti(currentAccountGroup.partnerId, '', [DynamicListsFeature]),
        accessService.canRunManualAutomations$,
        currentUserId$,
      ]);
    }),
    map(([accountGroupId, featureFlags, canRunAutomations, currentUserId]) => {
      const canAddToList = featureFlags[DynamicListsFeature];
      const actions: MultiRowAction[] = [];
      if (canAddToList) {
        actions.push(
          AddToListSelectAllAction(
            accountGroupId,
            listActionsService,
            'CustomObject',
            translationSvc.instant('CRM.ADD_TO_STATIC_LIST'),
          ),
        );
      }
      actions.push(
        BulkDeleteAll(accountGroupId, bulkDeleteService, 'CustomObject', 'LIST_OBJECTS_TABLE.BULK_DELETE.LABEL'),
      );
      actions.push(
        ExportSelectAllRowAction(accountGroupId, exportActionsService, 'CustomObject', 'ACTIONS.CRM.EXPORT.LABEL'),
      );
      if (canRunAutomations) {
        actions.push(
          AutomationSelectAllRowAction(
            accountGroupId,
            automationActionService,
            'CustomObject',
            'COMPANY.LIST_OBJECTS_TABLE.ACTIONS.START_AUTOMATION',
          ),
        );
        actions.push(
          EphemeralAutomationSelectAllRowAction(
            accountGroupId,
            automationActionService,
            currentUserId,
            'CustomObject',
            'COMPANY.LIST_OBJECTS_TABLE.ACTIONS.RUN_BULK_ACTION',
          ),
        );
      }
      return actions;
    }),
  );
}
